import { useEffect, useMemo, useState } from 'react'
import Highlight from '@/components/common/Highlight'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { studentsApi, type StudentResponse } from '@/api/endpoints/students'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { useUI } from '@/stores/ui'
import AppHeader from '@/components/common/AppHeader'
import ParentClassCalendarModal from '@/components/parent/ParentClassCalendarModal'
import MessageCenter from '@/components/messaging/MessageCenter'
import Spinner from '@/components/common/Spinner'
import AppFooter from '@/components/common/AppFooter'

interface ChildWithClass extends StudentResponse {
  classDetails?: ClassResponse | null
  teachers?: Array<{ first_name?: string; last_name?: string; title?: string }>
}

export default function ParentDashboardPage() {
  const { user } = useAuth()
  const addToast = useUI((state) => state.addToast)
  const [children, setChildren] = useState<ChildWithClass[]>([])
  const [selectedChildId, setSelectedChildId] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [showCalendar, setShowCalendar] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<{
    inNotes: boolean
    inEvents: boolean
    matchingEvents: string[]
  } | null>(null)

  // Fetch parent's children and their class information
  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!user) return
      setLoading(true)
      try {
        // Fetch children (students API filters to parent's children for PARENT role)
        const studentsData = await studentsApi.list()
        const childrenData = Array.isArray(studentsData) ? studentsData : []

        // Fetch class details for each child
        const childrenWithClasses: ChildWithClass[] = await Promise.all(
          childrenData.map(async (child) => {
            let classDetails: ClassResponse | null = null
            let teachers: Array<{ first_name?: string; last_name?: string; title?: string }> = []

            if (child.class_id) {
              try {
                const allClasses = await classesApi.list()
                const classes = Array.isArray(allClasses) ? allClasses : []
                classDetails = classes.find((c) => c.id === child.class_id) || null

                // Fetch teacher names if available
                if (classDetails?.teacher_names) {
                  // teacher_names is an array of strings (names)
                  teachers = classDetails.teacher_names.map((name) => {
                    // Try to parse "Title First Last" or "First Last"
                    const parts = name.split(' ')
                    if (parts.length >= 2) {
                      const last = parts[parts.length - 1]
                      const first = parts[parts.length - 2]
                      const title = parts.length > 2 ? parts.slice(0, -2).join(' ') : undefined
                      return { first_name: first, last_name: last, title }
                    }
                    return { first_name: name, last_name: '', title: undefined }
                  })
                }
              } catch (error) {
                console.error('Failed to load class details:', error)
              }
            }

            return {
              ...child,
              classDetails,
              teachers,
            }
          })
        )

        if (mounted) {
          setChildren(childrenWithClasses)
          if (childrenWithClasses.length > 0 && !selectedChildId) {
            setSelectedChildId(childrenWithClasses[0].id)
          }
        }
      } catch (error: any) {
        console.error('Failed to load children:', error)
        if (mounted) {
          addToast({
            message: error.response?.data?.detail || 'Failed to load your children. Please try again.',
            type: 'error',
          })
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => {
      mounted = false
    }
  }, [user, addToast, selectedChildId])

  const selectedChild = useMemo(() => {
    return children.find((c) => c.id === selectedChildId) || null
  }, [children, selectedChildId])

  // Search functionality
  useEffect(() => {
    if (!searchQuery.trim() || !selectedChild) {
      setSearchResults(null)
      return
    }

    const query = searchQuery.toLowerCase()
    const child = selectedChild
    const classDetails = child.classDetails

    let inNotes = false
    let inEvents = false
    const matchingEvents: string[] = []

    // Search in class notes
    if (classDetails?.notes && classDetails.notes.toLowerCase().includes(query)) {
      inNotes = true
    }

    // Search in events (we'd need to fetch events, but for now we'll check when calendar is opened)
    // This will be handled in the ParentClassCalendarModal

    setSearchResults({ inNotes, inEvents, matchingEvents })
  }, [searchQuery, selectedChild])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 page-transition">
        <AppHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Spinner label="Loading your children's classes..." />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      <AppHeader />
      <motion.main
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        {/* Header */}
        <div className="mb-6 pb-4 border-b-2 border-gray-200">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Parent Dashboard</h1>
          <p className="text-sm text-gray-600">View your children's classes, schedules, and messages</p>
        </div>

        {/* Child Selection */}
        {children.length === 0 ? (
          <div className="card p-8 rounded-xl border-2 border-gray-200 bg-white text-center">
            <div className="text-4xl mb-4">👨‍👩‍👧‍👦</div>
            <p className="text-lg font-semibold text-gray-700 mb-2">No Children Registered</p>
            <p className="text-sm text-gray-500">
              Your children will appear here once they are registered and approved by your parish administrator.
            </p>
          </div>
        ) : (
          <>
            {children.length > 1 && (
              <div className="card p-5 rounded-xl border-2 border-gray-200 bg-white mb-6">
                <label className="block text-sm font-semibold mb-2 text-gray-700">Select Child</label>
                <select
                  className="input-field w-full sm:w-80 focus-ring"
                  value={selectedChildId}
                  onChange={(e) => setSelectedChildId(e.target.value)}
                  aria-label="Select child"
                >
                  {children.map((child) => (
                    <option key={child.id} value={child.id}>
                      {child.first_name} {child.last_name}
                      {child.classDetails ? ` - ${child.classDetails.name}` : ' (No class assigned)'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {selectedChild && (
              <div className="space-y-6">
                {/* Child Preview Card */}
                <motion.div
                  className="card p-6 rounded-xl border-2 border-gray-200 bg-gradient-to-br from-white to-gray-50 shadow-lg"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                >
                  <div className="mb-4 pb-4 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">
                      {selectedChild.first_name} {selectedChild.last_name}
                    </h2>
                    {selectedChild.classDetails ? (
                      <p className="text-lg text-gray-600">{selectedChild.classDetails.name}</p>
                    ) : (
                      <p className="text-sm text-yellow-600 font-medium">⚠️ Not yet assigned to a class</p>
                    )}
                  </div>

                  {selectedChild.classDetails ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Teachers */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Teacher(s)</div>
                        <div className="text-base font-semibold text-gray-900">
                          {selectedChild.teachers && selectedChild.teachers.length > 0 ? (
                            selectedChild.teachers.map((teacher, idx) => (
                              <span key={idx}>
                                {teacher.title && `${teacher.title} `}
                                {teacher.first_name} {teacher.last_name}
                                {idx < selectedChild.teachers!.length - 1 && ', '}
                              </span>
                            ))
                          ) : (
                            <span className="text-gray-400">Not assigned</span>
                          )}
                        </div>
                      </div>

                      {/* Location */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">Class Location</div>
                        <div className="text-base font-semibold text-gray-900">
                          {selectedChild.classDetails.address || selectedChild.classDetails.location || (
                            <span className="text-gray-400">Not specified</span>
                          )}
                        </div>
                      </div>

                      {/* General Time */}
                      <div className="p-4 bg-white rounded-lg border border-gray-200 sm:col-span-2">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-1">General Time</div>
                        <div className="text-base font-semibold text-gray-900 mb-2">
                          {selectedChild.classDetails.weekly_time_description || (
                            <span className="text-gray-400">Not specified</span>
                          )}
                        </div>
                        <div className="text-xs text-red-600 font-medium mt-2">
                          ⚠️ NOTE: Classes may be rescheduled or cancelled for any given week — view the actual class
                          calendar to confirm.
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm text-yellow-800">
                        Your child has not been assigned to a class yet. Please contact your parish administrator for
                        class assignment.
                      </p>
                    </div>
                  )}
                </motion.div>

                {/* Search */}
                {selectedChild.classDetails && (
                  <div className="card p-5 rounded-xl border-2 border-gray-200 bg-white">
                    <label className="block text-sm font-semibold mb-2 text-gray-700">
                      Search Class Topics & Notes
                    </label>
                    <input
                      type="text"
                      className="input-field w-full focus-ring"
                      placeholder="Search for keywords in class topics, notes, and events..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      aria-label="Search class content"
                    />
                    {searchResults && (
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm text-blue-800">
                          {searchResults.inNotes && searchResults.inEvents
                            ? '✅ Found in class notes and events'
                            : searchResults.inNotes
                            ? '✅ Found in class notes'
                            : searchResults.inEvents
                            ? '✅ Found in events'
                            : '❌ No matches found'}
                        </p>
                      </div>
                    )}
                    {selectedChild.classDetails.notes && searchQuery && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="text-xs font-semibold text-gray-500 uppercase mb-2">Class Notes</div>
                        <div
                          className="text-sm text-gray-700 whitespace-pre-wrap"><Highlight text={selectedChild.classDetails.notes} query={searchQuery} /></div>
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                {selectedChild.classDetails && (
                  <div className="flex flex-wrap items-center gap-3">
                    <button
                      className="btn-primary focus-ring"
                      onClick={() => setShowCalendar(true)}
                      aria-label="View class calendar"
                    >
                      📅 View Class Calendar
                    </button>
                    <button
                      className="btn-secondary focus-ring"
                      onClick={async () => {
                        const cd = selectedChild.classDetails
                        if (!cd) return
                        const info = [
                          `Class: ${cd.name}`,
                          cd.weekly_time_description ? `When: ${cd.weekly_time_description}` : null,
                          (cd.address || cd.location) ? `Where: ${cd.address || cd.location}` : null,
                          cd.teacher_names && cd.teacher_names.length ? `Teacher(s): ${cd.teacher_names.join(', ')}` : null,
                        ].filter(Boolean).join('\n')
                        try {
                          await navigator.clipboard.writeText(info)
                          addToast({ message: 'Class information copied to clipboard', type: 'success' })
                        } catch {
                          addToast({ message: info, type: 'info' })
                        }
                      }}
                    >
                      📋 Class Information
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* Messages */}
        <div className="mt-8">
          <MessageCenter />
        </div>

        {/* Calendar Modal */}
        {selectedChild?.classDetails && (
          <ParentClassCalendarModal
            open={showCalendar}
            onClose={() => setShowCalendar(false)}
            classId={selectedChild.classDetails.id}
            className={selectedChild.classDetails.name}
            searchQuery={searchQuery}
          />
        )}
      </motion.main>
      <AppFooter />
    </div>
  )
}
